import type { DocComment } from '@affine/core/modules/comment/types';
import type { CommentTargetAnchor } from '@affine/core/modules/comment/types';
import {
  PDF_PAGE_COMMENT_COLOR,
  type PDFPageHighlightRect,
  resolvePdfHighlightColor,
} from '@affine/core/modules/pdf/views/pdf-highlight-overlay';
import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';

export type AttachmentPdfTextHighlight = {
  id: string;
  commentId?: string;
  sourceId: string;
  quote: string;
  color: string;
  createdAt: number;
  anchors: {
    pageIndex: number;
    rects: {
      x: number;
      y: number;
      width: number;
      height: number;
    }[];
  }[];
};

export function getStoredPdfTextHighlights(model?: AttachmentBlockModel) {
  if (!model) return [];
  const props = model.props as unknown as {
    pdfTextHighlights?: AttachmentPdfTextHighlight[];
  };
  return props.pdfTextHighlights ?? [];
}

export function useStoredPdfTextHighlights(model?: AttachmentBlockModel) {
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!model) return;
    const subscription = model.store.slots.blockUpdated.subscribe(payload => {
      if (payload.type !== 'update') return;
      if (payload.id !== model.id) return;
      const key = (payload.props as { key?: string } | undefined)?.key;
      if (key && key !== 'pdfTextHighlights') return;
      setVersion(current => current + 1);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [model]);

  return getStoredPdfTextHighlights(model);
}

export function getPdfCommentHighlightRects({
  comments,
  storedHighlights,
  blockId,
  sourceId,
  pageIndex,
  activeCommentId,
}: {
  comments: DocComment[];
  storedHighlights: AttachmentPdfTextHighlight[];
  blockId: string;
  sourceId: string;
  pageIndex: number;
  activeCommentId: string | null;
}): PDFPageHighlightRect[] {
  const commentRects = comments.flatMap(comment =>
    comment.targetAnchors.flatMap(anchor => {
      if (
        comment.resolved ||
        anchor.type !== 'pdf-text' ||
        anchor.blockId !== blockId ||
        anchor.sourceId !== sourceId ||
        anchor.pageIndex !== pageIndex
      ) {
        return [];
      }
      return anchor.rects.map(rect => ({
        ...rect,
        commentId: comment.id,
        color: resolvePdfHighlightColor(anchor.color, PDF_PAGE_COMMENT_COLOR),
        kind: 'comment' as const,
        active: activeCommentId === comment.id,
      }));
    })
  );
  const resolvedCommentIds = new Set(
    comments.flatMap(comment => (comment.resolved ? [comment.id] : []))
  );
  const commentHighlightIds = new Set(
    comments.flatMap(comment =>
      comment.targetAnchors.some(
        anchor =>
          anchor.type === 'pdf-text' &&
          anchor.blockId === blockId &&
          anchor.sourceId === sourceId
      )
        ? [comment.id]
        : []
    )
  );
  const storedRects = storedHighlights.flatMap(highlight => {
    if (highlight.sourceId !== sourceId) return [];
    if (highlight.commentId && resolvedCommentIds.has(highlight.commentId)) {
      return [];
    }
    if (highlight.commentId && commentHighlightIds.has(highlight.commentId)) {
      return [];
    }
    return highlight.anchors
      .filter(anchor => anchor.pageIndex === pageIndex)
      .flatMap(anchor =>
        anchor.rects.map(rect => ({
          ...rect,
          commentId: highlight.commentId,
          highlightId: highlight.commentId ? undefined : highlight.id,
          color: highlight.commentId
            ? resolvePdfHighlightColor(highlight.color, PDF_PAGE_COMMENT_COLOR)
            : resolvePdfHighlightColor(highlight.color),
          kind: highlight.commentId
            ? ('comment' as const)
            : ('highlight' as const),
          active:
            Boolean(highlight.commentId) &&
            activeCommentId === highlight.commentId,
        }))
      );
  });
  return [...storedRects, ...commentRects];
}

export function persistPdfTextHighlight({
  model,
  anchors,
  color,
  commentId,
}: {
  model?: AttachmentBlockModel;
  anchors: Extract<CommentTargetAnchor, { type: 'pdf-text' }>[];
  color: string;
  commentId?: string;
}) {
  if (!model) return;
  if (anchors.length === 0) return;
  const sourceId = model.props.sourceId;
  if (!sourceId) return;
  const highlight: AttachmentPdfTextHighlight = {
    id: commentId ?? nanoid(),
    commentId,
    sourceId,
    quote: anchors[0].quote,
    color,
    createdAt: Date.now(),
    anchors: anchors.map(anchor => ({
      pageIndex: anchor.pageIndex,
      rects: anchor.rects,
    })),
  };
  const current = getStoredPdfTextHighlights(model).filter(item => {
    if (item.id === highlight.id) return false;
    if (commentId && item.commentId === commentId) return false;
    return true;
  });
  model.store.updateBlock(model, {
    pdfTextHighlights: [...current, highlight],
  } as Partial<AttachmentBlockModel['props']>);
}

export function removePdfTextHighlight({
  model,
  highlightId,
}: {
  model?: AttachmentBlockModel;
  highlightId: string;
}) {
  if (!model) return;
  const current = getStoredPdfTextHighlights(model);
  const next = current.filter(item => item.id !== highlightId);
  if (next.length === current.length) return;
  model.store.updateBlock(model, {
    pdfTextHighlights: next.length > 0 ? next : undefined,
  } as Partial<AttachmentBlockModel['props']>);
}

export function removePdfTextHighlightForComment({
  model,
  commentId,
}: {
  model?: AttachmentBlockModel;
  commentId: string;
}) {
  if (!model) return;
  const current = getStoredPdfTextHighlights(model);
  const next = current.filter(item => item.commentId !== commentId);
  if (next.length === current.length) return;
  model.store.updateBlock(model, {
    pdfTextHighlights: next.length > 0 ? next : undefined,
  } as Partial<AttachmentBlockModel['props']>);
}
